// Creators — 앱 셸 (스캐폴드).
//
// 골조만 있다: 좌측 사이드바(크리에이터 히어로 + 검색 + 6메뉴) + 우측 섹션 전환.
// 각 섹션은 아직 빈 방(SectionPlaceholder) — 담당(혜원/현정)이 채운다.
//
// 공유 파일: 섹션 목록/라우팅/셸은 여기(P0). 변경은 작은 PR + 양쪽 리뷰.
// 디자인: 실제 스타일은 packages/design-system 토큰으로 각 섹션 작업 시 적용.
//   (여기 클래스는 스캐폴드용 최소 스타일)
import { createContext, useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Download, Link as LinkIcon, Search } from 'lucide-react'
import { fetchCreator, type CreatorSummary, type Platform } from './creatorsApi'
import { isPlatform, pickInitialPlatform } from './sections/platformDefault'
import { AiJudgedNotice } from './sections/AiJudgedNotice'
import { hexA } from './sections/overview/format'
import { formatHandle } from './sections/profile/format'
import { BrandLinkModal } from './BrandLinkModal'
import { ExportPdfModal } from './ExportPdfModal'
import { OverviewSection } from './sections/OverviewSection'
import { ProfileSection } from './sections/ProfileSection'
import { PerformanceSection } from './sections/PerformanceSection'
import { ProductsSection } from './sections/ProductsSection'
import { CampaignsSection } from './sections/CampaignsSection'
import { FinancialSection } from './sections/FinancialSection'

// 섹션이 셸 헤더 하단(타이틀 아래)에 탭/액션을 portal 할 슬롯 (혜원 Campaigns Board·Calendar·History
// 탭이 소비 — feat/creators 머지에서 이식). null이면 아직 미장착.
export const CreatorsHeaderSlotContext = createContext<HTMLElement | null>(null)

// 사이드바 순서 = 디자인 시안과 동일.
type SectionKey = 'overview' | 'profile' | 'performance' | 'products' | 'campaigns' | 'financial'

/** IG·TikTok 협찬 판정값이 실제로 뜨는 섹션 — 여기에만 AI 판독 고지를 건다. */
const AI_NOTICE_SECTIONS = new Set<SectionKey>(['overview', 'profile', 'performance'])

const NAV: { key: SectionKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'profile', label: 'Profile' },
  { key: 'performance', label: 'Performance' },
  { key: 'products', label: 'Products' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'financial', label: 'Financial' },
]


// 사이드바 히어로 하단 스크림 — 이름 글래스 박스 가독성용. ink 토큰에서 파생(인라인 hex 아님).
const HERO_SCRIM =
  'linear-gradient(180deg, transparent 0%, color-mix(in srgb, var(--ink-800) 72%, transparent) 100%)'

export default function CreatorsPage() {
  const { creatorId } = useParams<{ creatorId: string }>()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const urlSection = params.get('section') as SectionKey | null
  const [section, setSection] = useState<SectionKey>(
    NAV.some((n) => n.key === urlSection) ? (urlSection as SectionKey) : 'overview',
  )
  const [creator, setCreator] = useState<CreatorSummary | null>(null)
  const [creatorStatus, setCreatorStatus] = useState<'loading' | 'ok' | 'error'>('loading')
  const [switchQuery, setSwitchQuery] = useState('')
  const [brandLinkOpen, setBrandLinkOpen] = useState(false)
  const [pdfExportOpen, setPdfExportOpen] = useState(false)
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!creatorId) return
    let alive = true
    setCreator(null)
    setCreatorStatus('loading')
    fetchCreator(creatorId)
      .then((c) => {
        if (!alive) return
        setCreator(c)
        setCreatorStatus('ok')
      })
      .catch(() => {
        if (!alive) return
        setCreator(null)
        setCreatorStatus('error')
      })
    return () => {
      alive = false
    }
  }, [creatorId])

  // 워크스페이스 진입/크리에이터 전환 시 스크롤 상단으로(목록의 이전 스크롤 위치 유지 방지).
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [creatorId])

  // id 없으면 목록으로(하드코딩 기본 id 제거 — 목록→선택 구조).
  if (!creatorId) return <Navigate to="/creators" replace />
  const id: string = creatorId // 가드 이후 narrowing(중첩 함수 클로저에서 사용)

  // "다른 크리에이터 검색" → 목록 페이지로 이동(입력값을 q로).
  function goSearch() {
    const q = switchQuery.trim()
    navigate(q ? `/creators?q=${encodeURIComponent(q)}` : '/creators')
  }

  function go(key: SectionKey) {
    setSection(key)
    const next = new URLSearchParams(params)
    next.set('section', key)
    setParams(next, { replace: true })
  }

  // 플랫폼 선택은 셸이 URL 로 쥔다 — `?section=` 은 URL 에 있는데 `?platform=` 만 없으면
  // 섹션을 옮기거나 새로고침할 때마다 선택이 리셋되고, 링크를 공유해도 안 따라간다.
  //   · 파라미터가 있으면 그 값이 정답이고(**고정**), 자동 보정은 더 이상 돌지 않는다.
  //   · 없으면 셸이 가진 channels[] 로 추측하고, 각 섹션이 첫 응답에서 한 번 보정할 수 있다.
  const platformPinned = isPlatform(params.get('platform'))
  const platform: Platform = platformPinned
    ? (params.get('platform') as Platform)
    : pickInitialPlatform(creator?.channels)
  function setPlatform(next: Platform) {
    const q = new URLSearchParams(params)
    q.set('platform', next)
    setParams(q, { replace: true })
  }


  const active = NAV.find((n) => n.key === section) ?? NAV[0]

  // 사이드바 히어로 — 크리에이터 themeColor(데이터값)에서 hexA로 틴트/글래스 파생(ProfileHero와 동일 패턴).
  const theme = creator?.themeColor
  const themeAccent = theme && hexA(theme, 1) ? theme : 'var(--brand-pink)'
  const heroTint =
    theme && hexA(theme, 0.55)
      ? `radial-gradient(120% 130% at 100% 0%, ${hexA(theme, 0.55)} 0%, ${hexA(theme, 0.12)} 42%, transparent 68%)`
      : null
  const glassBorder = (theme && hexA(theme, 0.55)) || 'var(--border-on-dark)'
  const glassBg = (theme && hexA(theme, 0.16)) || 'color-mix(in srgb, var(--paper) 12%, transparent)'

  // 히어로 글래스 서브라인 — 시안 형식 "{레이블} · {핸들}". creator.handle 은 대표 채널(구독자 최대)
  // 소셜네임이라, 그 채널이 IG/TikTok이면 @프리픽스·YouTube면 채널명 그대로(formatHandle).
  const repChannel = (creator?.channels ?? []).reduce(
    (best, c) => (best && best.subscribers >= c.subscribers ? best : c),
    null as CreatorSummary['channels'][number] | null,
  )
  const heroSubline = creator
    ? [creator.label, formatHandle(repChannel?.platform ?? 'youtube', creator.handle)]
        .map((s) => s?.trim())
        .filter(Boolean)
        .join(' · ')
    : creatorStatus === 'error'
      ? '크리에이터를 찾을 수 없습니다'
      : '로딩 중…'

  // 섹션 렌더 — 플랫폼 선택은 셸이 URL 로 쥐고 세 섹션이 공유한다(섹션을 옮겨도 유지).
  function renderSection(key: SectionKey) {
    const platformProps = { platform, platformPinned, onPlatformChange: setPlatform }
    switch (key) {
      case 'overview':
        return <OverviewSection creatorId={id} creator={creator} {...platformProps} />
      case 'profile':
        return <ProfileSection creatorId={id} {...platformProps} />
      case 'performance':
        return <PerformanceSection creatorId={id} {...platformProps} />
      case 'products':
        return <ProductsSection creatorId={id} />
      case 'campaigns':
        return <CampaignsSection creatorId={id} />
      case 'financial':
        return <FinancialSection creatorId={id} />
    }
  }

  return (
    // 워크스페이스 배경 = 연보라 #F6F3F9 (사용자 지정). 포털 main 의 max-w-1320 컨테이너를 full-bleed 로 탈출
    // (핸드오프처럼 사이드바 좌측 밀착 + 콘텐츠 뷰포트 전체 폭). 흰 카드/사이드바가 연보라 위에 레이어.
    <div className="mx-[calc(50%-50vw)] w-screen bg-[#F6F3F9]">
      {/* ── 브레드크럼 바 (전체폭) ── */}
      <div className="flex items-center gap-[11px] border-b border-line-hairline bg-paper px-7 py-3">
        {/* 뒤로가기 = 크리에이터 목록. 홈(포털)이 아니라 목록으로 — 사용자 확정 */}
        <Link
          to="/creators"
          className="inline-flex items-center gap-2 font-text text-[14px] font-medium text-fg-2 hover:text-fg-1"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          For Brand Intelligence Consulting
        </Link>
        <span className="text-ink-300">/</span>
        <span className="font-text text-[14px] font-medium text-fg-1">Creators</span>
      </div>

      <div className="flex min-h-[calc(100vh-103px)]">
        {/* ── 사이드바 ── */}
        <aside className="flex w-[248px] flex-none flex-col border-r border-line-hairline bg-paper">
          <div className="border-b border-line-hairline">
            {/* 크리에이터 히어로 */}
            <div className="relative h-72 overflow-hidden bg-ink-800">
              {creator?.photoUrl && (
                <img
                  src={creator.photoUrl}
                  alt=""
                  aria-hidden
                  className="absolute inset-0 h-full w-full object-cover"
                  onError={(e) => {
                    ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                  }}
                />
              )}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[70%]" style={{ background: HERO_SCRIM }} />
              {heroTint && (
                <div className="pointer-events-none absolute inset-0 opacity-70" style={{ background: heroTint }} />
              )}
              {/* 좌상단 eyebrow */}
              <div className="pointer-events-none absolute left-3.5 top-3.5 flex items-center gap-2">
                <span className="inline-block h-[5px] w-[5px]" style={{ background: themeAccent }} aria-hidden />
                <span className="font-text text-[11px] font-medium tracking-[0.18em] text-white/90">CREATOR</span>
              </div>
              {/* 하단 글래스 이름 박스 */}
              <div
                className="pointer-events-none absolute inset-x-3.5 bottom-3.5 border p-[11px_13px] backdrop-blur-[16px] backdrop-saturate-[1.3]"
                style={{ borderColor: glassBorder, background: glassBg }}
              >
                <div className="font-display text-[24px] font-bold leading-none text-white">
                  {creator?.name ?? (creatorStatus === 'error' ? '데이터 없음' : '크리에이터')}
                  <span style={{ color: themeAccent }}>.</span>
                </div>
                <div className="mt-[5px] truncate font-text text-[11px] text-white/80">
                  {heroSubline}
                </div>
              </div>
            </div>
            {/* 다른 크리에이터 검색 → 목록(/creators?q=)로 이동 */}
            <div className="p-4">
              <div className="flex items-center gap-2 border border-line-soft px-[10px] py-2 focus-within:border-brand-blue">
                <button type="button" onClick={goSearch} aria-label="검색" className="flex-none">
                  <Search className="h-3.5 w-3.5 text-fg-3" aria-hidden />
                </button>
                <input
                  value={switchQuery}
                  onChange={(e) => setSwitchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') goSearch()
                  }}
                  placeholder="다른 크리에이터 검색"
                  aria-label="다른 크리에이터 검색"
                  className="min-w-0 flex-1 bg-transparent font-text text-xs text-fg-1 placeholder:text-fg-3 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* 6메뉴 */}
          <nav className="flex-1 p-[16px_12px]">
            {NAV.map((n) => {
              const on = n.key === section
              return (
                <button
                  key={n.key}
                  onClick={() => go(n.key)}
                  className={[
                    'mb-1 block w-full border-l-[3px] p-[12px_13px] text-left font-display text-[18px]',
                    'font-medium tracking-[0.01em] transition-colors hover:bg-ink-100 hover:text-ink-800',
                    on ? 'border-brand-blue bg-ink-50 text-ink-800' : 'border-transparent text-fg-2',
                  ].join(' ')}
                >
                  {n.label}
                </button>
              )
            })}
          </nav>
        </aside>

        {/* ── 메인 ── */}
        <main className="min-w-0 flex-1">
          {/* 헤더 — 사이드바 우측 영역을 full-width 로 채운다(폭 제한 없음). */}
          <div className="border-b border-line-hairline bg-paper">
            <div className="flex items-center justify-between px-8 py-5">
              <div>
                <div className="font-text text-xs text-fg-3">
                  {creator ? `Creators · ${creator.name}` : 'Creators'}
                </div>
                <div className="mt-0.5 font-display text-2xl font-medium text-ink-800">
                  {active.label}
                  <span className="text-brand-blue">.</span>
                </div>
                {/* AI 판독 고지 — **채널 탭과 무관하게 항상** 보인다. 탭 안에만 두면 YouTube 를
                    보는 동안 고지가 사라져, 화면을 훑는 사람이 이 워크스페이스에 AI 판독값이
                    섞여 있다는 사실 자체를 모르고 지나간다. 문구는 AiJudgedNotice 가 단일 원천.
                    IG·TikTok 값이 실제로 뜨는 세 섹션에만 붙인다(Products·Campaigns·Financial 은
                    협찬 판정을 쓰지 않으므로 붙이면 그게 거짓말이다). */}
                {AI_NOTICE_SECTIONS.has(section) && <AiJudgedNotice className="mt-1.5" />}
              </div>
              {/* 헤더 액션은 Overview 탭 전용 — 소개서 산출물의 진입점을 한 곳으로 모은다(사용자 확정) */}
              {section === 'overview' && (
                <div className="flex items-center gap-2.5">
                  <button
                    onClick={() => setPdfExportOpen(true)}
                    className="inline-flex h-[38px] items-center gap-2 border border-line-soft bg-paper px-[15px] font-text text-[14px] font-medium text-fg-1"
                  >
                    <Download className="h-[15px] w-[15px]" aria-hidden />
                    Export PDF
                  </button>
                  <button
                    onClick={() => setBrandLinkOpen(true)}
                    className="inline-flex h-[38px] items-center gap-2 bg-brand-blue px-[17px] font-text text-[14px] font-medium text-white"
                  >
                    Generate brand link
                    <LinkIcon className="h-[15px] w-[15px]" aria-hidden />
                  </button>
                </div>
              )}
            </div>
            {/* 섹션이 헤더 하단에 탭/액션을 portal (예: Campaigns Board·Calendar·History) — 없으면 비어있음 */}
            <div ref={setHeaderSlot} className="px-8" />
          </div>

          {/* 활성 섹션 — full-width(폭 제한 제거), 콘텐츠 패딩 32/28/80 */}
          <CreatorsHeaderSlotContext.Provider value={headerSlot}>
            <div className="px-8 pt-7 pb-20">{renderSection(active.key)}</div>
          </CreatorsHeaderSlotContext.Provider>
        </main>
      </div>

      <BrandLinkModal
        open={brandLinkOpen}
        creatorId={id}
        channels={creator?.channels}
        onClose={() => setBrandLinkOpen(false)}
      />
      <ExportPdfModal
        open={pdfExportOpen}
        creatorId={id}
        channels={creator?.channels}
        onClose={() => setPdfExportOpen(false)}
      />
    </div>
  )
}
